import { EditableComponent, ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { FC, Suspense, lazy, useEffect, useRef, useCallback, memo } from 'react';
import clsx from 'clsx';
import { CardHorizontalMini, IconBlock, Heading, TabComponent, Types, canUseDOM } from '@marriott/mi-ui-library';

import { StyledCobrandContainer } from './CobrandContainer.styles';
import { CobrandContainerProps } from './CobrandContainer.types';
import { PointsEarned } from '../PointsEarned';
import { CardOverview } from '../CardOverview';
import { StickyCard } from '../StickyCard';
import { BANK_LANDING_TAB_LIST as allTabList, ColumnLayout } from '../../modules/constants/constants';
import { equalizeBankLandingCards, removeExtraHeights, getColumnClass, AEMReactCompMap } from '../../modules/utils';
import { useStore } from '../../modules/store/cobrandStore';

const useDebounce = (callback: () => void, delay: number) => {
  const timeoutRef = useRef<NodeJS.Timeout>();

  return useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(callback, delay);
  }, [callback, delay]);
};

type CardItemProps = {
  cardName: string;
  cqItems: Record<string, unknown>;
  index: number;
  columnLayout: number;
  variant: string;
  isBonusPointsLabelVisible?: boolean;
  mapper: Record<string, string>;
  activeTabValue?: string;
};

const CardItem = memo<CardItemProps>(
  ({ cardName, cqItems, index, columnLayout, variant, isBonusPointsLabelVisible, mapper }) => {
    const card = cqItems[cardName] as Record<string, unknown> | undefined;
    if (!card) return null;

    const returnable: JSX.Element[] = [];
    const cardType = index + 1 > 2 ? 'business' : 'personal';
    const cardItems = card[':items'] as Record<string, unknown>;
    let hasCardOverview = false;
    let rowCount = 1;

    (card[':itemsOrder'] as string[]).forEach((itemKey: string) => {
      const item = cardItems[itemKey];
      if (!item) return;

      const itemTyped = item as Record<string, unknown>;

      if (itemKey.includes('cardoverview')) {
        hasCardOverview = true;
        returnable.push(
          <CardOverview
            key={itemKey}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {...(itemTyped as any)}
            column={`${columnLayout}`}
            customClass={`overview-${rowCount++}`}
          />
        );
      } else if (itemKey.includes('points_section')) {
        const enablePointDetails = (itemTyped['enablePointDetailsSection'] as string) === 'true';
        const pointsClass = enablePointDetails ? 'detail' : 'spend';
        const customClass = pointsClass === 'spend' ? (rowCount > 1 ? 'mt-5' : 'pt-lg-3') : '';

        returnable.push(
          <PointsEarned
            key={itemKey}
            column={`${columnLayout}`}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {...(itemTyped as any)}
            customClass={`${customClass} ${pointsClass}-${rowCount++}`}
            isBonusPointsLabelVisible={isBonusPointsLabelVisible ?? false}
          />
        );
      } else if (itemKey.includes('cardhorizontalmini')) {
        returnable.push(
          <div key={itemKey} className="py-2 pr-3 d-flex">
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <CardHorizontalMini {...(itemTyped as any)} />
          </div>
        );
      } else {
        const itemType = (itemTyped[':type'] as string)?.split('/').pop();
        const innerComp = mapper[itemType || ''];
        if (innerComp) {
          const Component = lazy(() =>
            import(`../${innerComp}/index`).then(module => ({
              default: module[innerComp],
            }))
          );
          returnable.push(
            <Suspense key={itemKey} fallback={<div />}>
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              <Component {...(itemTyped as any)} />
            </Suspense>
          );
        }
      }
    });

    const columnClass = clsx(
      variant === 'blp'
        ? `${getColumnClass(`${columnLayout}-column` as ColumnLayout, cardType)} p-0 m-0 px-2`
        : 'col-12 col-md-6 col-lg-4 d-flex flex-column',
      hasCardOverview && cardType === 'business' && columnLayout === 3 && 'd-flex justify-content-center'
    );

    return (
      <div key={`card-component-${cardName}`} className={columnClass}>
        {returnable}
      </div>
    );
  },
  (prev, next) => {
    return (
      prev.cardName === next.cardName &&
      prev.index === next.index &&
      prev.columnLayout === next.columnLayout &&
      prev.variant === next.variant &&
      prev.activeTabValue === next.activeTabValue &&
      prev.isBonusPointsLabelVisible === next.isBonusPointsLabelVisible
    );
  }
);

CardItem.displayName = 'CardItem';

export const CobrandContainerConfig = {
  emptyLabel: 'CobrandContainer',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/cobrandcontainer`,
};

export const CobrandContainerComponent: FC<CobrandContainerProps> = ({
  header,
  columnLayout,
  cqItems,
  containerId,
  itemPath,
  pagePath,
  totalNumberOfChildComponents,
  isAuthorMode,
  enableClickTrack,
  isStickyCardEnabled,
  variant = 'default-layout',
  cardCount = 2,
  description,
  bgImage = '',
  styleclass,
  isChaseButton,
  isAmexButton,
  isTertiaryLinksVisible,
  isImagehidden,
  isPointDescriptionVisible,
  isBonusPointsLabelVisible,
  isAnnualFeeVisible,
  allowedComponents,
}) => {
  const mapper = AEMReactCompMap(allowedComponents);
  const containerRef = useRef<HTMLDivElement>(null);
  const stickyComponentRef = useRef<HTMLDivElement>(null);
  const cardOverviewRef = useRef<HTMLDivElement>(null);
  const { setCobrandData, cobrandData } = useStore(store => store);

  const replaceHorizontalMiniClass = () => {
    if (!canUseDOM || !containerRef.current) return;

    const cardHorizontalMiniList = containerRef.current.querySelectorAll('.miniCardContent .t-font-alt-s');
    cardHorizontalMiniList.forEach(cardHorizontalEl => {
      cardHorizontalEl.classList.remove('t-font-alt-s');
      cardHorizontalEl.classList.add('t-font-alt-xs');
    });
  };

  const screenResizeHandler = useCallback(() => {
    if (variant === 'default-layout') {
      replaceHorizontalMiniClass();
    } else if (variant === 'blp') {
      removeExtraHeights(containerRef);
      equalizeBankLandingCards(containerRef);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  const debouncedResizeHandler = useDebounce(screenResizeHandler, 150);

  useEffect(() => {
    if (!containerRef.current) return;

    equalizeBankLandingCards(containerRef);
    if (variant === 'default-layout') {
      replaceHorizontalMiniClass();
    }

    if (typeof ResizeObserver === 'undefined') {
      if (typeof window !== 'undefined') {
        window.addEventListener('resize', screenResizeHandler);
        return () => {
          window.removeEventListener('resize', screenResizeHandler);
        };
      }
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      debouncedResizeHandler();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedResizeHandler]);

  useEffect(() => {
    if (variant === 'blp') {
      removeExtraHeights(containerRef);
      equalizeBankLandingCards(containerRef);
    }
  }, [cobrandData.activeTabValue, variant]);

  const getCardCount = (jsonData: typeof cqItems) => {
    const allCardTypes = { personal: 0, business: 0 };

    if (columnLayout !== 3) return allCardTypes;

    Object.entries(jsonData).forEach(([, card]) => {
      const cardItems = card[':items'];
      Object.entries(cardItems).forEach(([itemKey, item]) => {
        if (itemKey.includes('cardoverview') && 'cardType' in item && item.cardType) {
          const cardType = item.cardType as 'personal' | 'business';
          if (cardType in allCardTypes) {
            allCardTypes[cardType]++;
          }
        }
      });
    });

    return allCardTypes;
  };

  const allCardTypes = getCardCount(cqItems);

  const getTabList = () => {
    return allTabList.map(tabItem => ({
      ...tabItem,
      tabSubtitle: `${allCardTypes[tabItem.tabValue as 'personal' | 'business']} Card${
        allCardTypes[tabItem.tabValue as 'personal' | 'business'] === 1 ? '' : 's'
      }`,
    }));
  };

  const getCardDetails = (jsonData: typeof cqItems) => {
    if (!isStickyCardEnabled) return [];

    const cardDetails: Array<Record<string, unknown>> = [];
    let count = 0;

    const reversedEntries = Object.entries(jsonData).reverse();

    for (const [, card] of reversedEntries) {
      if (count >= columnLayout) break;

      const cardItems = card[':items'];
      for (const [itemKey, item] of Object.entries(cardItems)) {
        if (itemKey.includes('cardoverview') && count < columnLayout) {
          const cardItem = item as Record<string, unknown>;
          const cardTypeValue = cardItem['cardType'];
          let normalizedCardType: 'business' | 'personal' | 'none' = 'none';
          if (cardTypeValue === 'business' || cardTypeValue === 'personal' || cardTypeValue === 'none') {
            normalizedCardType = cardTypeValue;
          }

          cardDetails.push({
            componentId: cardItem['componentId'],
            imagePath: cardItem['imagePath'],
            altText: cardItem['altText'],
            header: cardItem['header'],
            linkText: cardItem['linkText'],
            linkUrl: cardItem['linkUrl'],
            openInaNewTab: cardItem['openInaNewTab'],
            trackingProperties: { ...(cardItem['trackingProperties'] as object), isCoBrand: true },
            columnCount: `${columnLayout}`,
            cardType: normalizedCardType,
            tertiaryLinks: cardItem['tertiaryLinks'],
            pointSectionDescription: cardItem['pointSectionDescription'],
          });
          count++;
        }
      }
    }

    return cardDetails;
  };

  const cardContainer = (index: number) => {
    const cardType = index + 1 > 2 ? 'business' : 'personal';
    const columnClass =
      variant === 'continent' ? '' : getColumnClass(`${columnLayout}-column` as ColumnLayout, cardType);

    const gridProps = {
      gridClassNames: 'col-12',
      columnClassNames: { responsivegrid: 'col-12' },
      customClassName: 'p-0 m-0',
      isInEditor: isAuthorMode,
      pagePath,
      itemPath: `${itemPath}/${totalNumberOfChildComponents?.[index]}`,
      allowedComponents: allowedComponents as never,
      config: {
        isEmpty: () => true,
        resourceType: 'mi-aem-common-spa/components/container',
      },
    };

    return (
      <div key={`card-container-${index}`} className={columnClass}>
        <ResponsiveGrid {...(gridProps as Parameters<typeof ResponsiveGrid>[0])} />
      </div>
    );
  };

  const cardContainerDefault = (index: number) => {
    const gridProps = {
      gridClassNames: 'col-12',
      columnClassNames: { responsivegrid: 'col-12' },
      customClassName: 'p-0 m-0 py-2',
      isInEditor: isAuthorMode,
      allowedComponents: allowedComponents as never,
      pagePath,
      itemPath: `${itemPath}/${totalNumberOfChildComponents?.[index]}`,
      config: {
        isEmpty: () => true,
        resourceType: 'mi-aem-common-spa/components/container',
      },
    };

    return (
      <div key={`card-container-default-${index}`} className="col-12 col-md-6 col-xl-4">
        <ResponsiveGrid {...(gridProps as Parameters<typeof ResponsiveGrid>[0])} />
      </div>
    );
  };

  const cardComponentDefault = (cardName: string, jsonData: typeof cqItems, index: number) => {
    const card = jsonData[cardName];
    if (!card) return null;

    const cardItems = card[':items'];

    for (const [itemKey, item] of Object.entries(cardItems)) {
      const itemTyped = item as Record<string, unknown>;

      if (itemKey.includes('cardhorizontalmini')) {
        return (
          <div key={index} className="col-12 col-md-6 col-xl-4 py-2 pr-2">
            <CardHorizontalMini
              {...(itemTyped as any)} // eslint-disable-line @typescript-eslint/no-explicit-any
              styleclass={`${itemTyped['styleclass'] || ''} cobrand-container__tile`}
            />
          </div>
        );
      }

      if (itemKey.includes('iconblock')) {
        return (
          <div key={index} className="col-12 col-md-6 col-xl-4 py-2">
            <IconBlock
              {...(itemTyped as any)} // eslint-disable-line @typescript-eslint/no-explicit-any
              styleclass="standard t-background-color cobrand-container__tile col-12"
            />
          </div>
        );
      }

      if (itemKey.includes('points_section')) {
        return (
          <PointsEarned
            key={index}
            {...(itemTyped as any)} // eslint-disable-line @typescript-eslint/no-explicit-any
            isBonusPointsLabelVisible={isBonusPointsLabelVisible ?? false}
          />
        );
      }

      const itemType = (itemTyped[':type'] as string)?.split('/').pop();
      const innerComp = mapper[itemType || ''];
      if (innerComp) {
        const Component = lazy(() =>
          import(`../${innerComp}/index`).then(module => ({
            default: module[innerComp],
          }))
        );
        return (
          <Suspense key={index} fallback={<div />}>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <Component {...(itemTyped as any)} />
          </Suspense>
        );
      }
    }

    return null;
  };

  return (
    <StyledCobrandContainer
      className={clsx(
        'container',
        'cobrand-container',
        'py-3 mt-md-3 mt-1',
        variant === 'blp' && 'px-0',
        variant === 'default-layout' && 'p-0 m-0',
        bgImage && variant === 'continent' && 'cobrand-container--image',
        styleclass
      )}
      data-component-name="o-cobrand-CobrandContainer"
      data-testid="cobrand-CobrandContainer"
      ref={containerRef}
      bgImage={variant === 'continent' ? bgImage : ''}
      id={containerId}
    >
      {header && (
        <Heading
          titleText={header}
          variation={Types.headingType.title}
          fontSize={variant === 'blp' ? Types.size.small : Types.size.medium}
          customClass={clsx(
            variant === 'blp' && 'text-md-left',
            variant !== 'default-layout' && 'mt-lg-4 mt-md-2 mt-0',
            'text-center mb-md-4 mb-lg-5'
          )}
        />
      )}

      {description && (
        <Heading
          titleText={description}
          variation={Types.headingType.subtitle}
          fontSize={Types.size.large}
          customClass="text-center mb-5 pb-3"
        />
      )}

      {allCardTypes.personal > 0 && (
        <div className="cobrand-container-tabs d-block d-md-none">
          <TabComponent
            tabList={getTabList()}
            setSelectedTab={(activeTabValue: string) => {
              setCobrandData({ ...cobrandData, activeTabValue: activeTabValue as never, selectedCards: [] });
            }}
            customClass=""
            disabledContainerClass
          />
        </div>
      )}

      {isStickyCardEnabled && (
        <StickyCard
          cardDetails={getCardDetails(cqItems) as never[]}
          ref={stickyComponentRef}
          cardOverviewRef={cardOverviewRef}
          isChaseButton={isChaseButton}
          isAmexButton={isAmexButton}
          isTertiaryLinksVisible={isTertiaryLinksVisible}
          isImagehidden={isImagehidden}
          isPointDescriptionVisible={isPointDescriptionVisible}
          isAnnualFeeVisible={isAnnualFeeVisible}
        />
      )}

      <div
        className={clsx(
          variant === 'blp'
            ? `row justify-content-around pb-lg-5 ${columnLayout === 2 ? 'two-column' : 'three-column'}`
            : 'row d-flex'
        )}
        data-is-click-track={enableClickTrack}
        ref={cardOverviewRef}
      >
        {variant === 'blp' && isAuthorMode && Array.from({ length: columnLayout }, (_, i) => cardContainer(i))}
        {variant === 'blp' &&
          !isAuthorMode &&
          totalNumberOfChildComponents?.map((cardName, index) => (
            <CardItem
              key={cardName}
              cardName={cardName}
              cqItems={cqItems}
              index={index}
              columnLayout={columnLayout}
              variant={variant}
              isBonusPointsLabelVisible={isBonusPointsLabelVisible}
              mapper={mapper}
              activeTabValue={cobrandData.activeTabValue}
            />
          ))}

        {variant === 'continent' &&
          isAuthorMode &&
          Array.from({ length: totalNumberOfChildComponents?.length || 0 }, (_, i) => cardContainerDefault(i))}
        {variant === 'continent' &&
          !isAuthorMode &&
          totalNumberOfChildComponents?.map((cardName, index) => (
            <CardItem
              key={cardName}
              cardName={cardName}
              cqItems={cqItems}
              index={index}
              columnLayout={columnLayout}
              variant={variant}
              isBonusPointsLabelVisible={isBonusPointsLabelVisible}
              mapper={mapper}
              activeTabValue={cobrandData.activeTabValue}
            />
          ))}

        {variant === 'default-layout' &&
          isAuthorMode &&
          Array.from({ length: cardCount }, (_, i) => cardContainerDefault(i))}
        {variant === 'default-layout' &&
          !isAuthorMode &&
          totalNumberOfChildComponents?.map((cardName, index) => cardComponentDefault(cardName, cqItems, index))}
      </div>
    </StyledCobrandContainer>
  );
};

CobrandContainerComponent.displayName = 'CobrandContainerComponent';

export const CobrandContainerEditableComponent: FC<CobrandContainerProps> = props => {
  return (
    <EditableComponent config={CobrandContainerConfig} {...props}>
      <CobrandContainerComponent {...props} />
    </EditableComponent>
  );
};

CobrandContainerEditableComponent.displayName = 'CobrandContainerEditableComponent';

export const CobrandContainer: FC<{ model?: CobrandContainerProps }> = props => {
  const { model } = props;
  if (!model) return null;

  return (
    <div
      className={clsx(
        model.styleclass?.includes('fullbleed') && 'm-container-fullbleed',
        model.styleclass?.includes('alternate') && 'alternate'
      )}
      data-testid="card-container"
    >
      <div className={clsx('container', !model.styleclass?.includes('fullbleed') && 'p-0')}>
        <CobrandContainerEditableComponent {...model} {...props} />
      </div>
    </div>
  );
};

CobrandContainer.displayName = 'CobrandContainer';
